import type { WorkspaceService } from '.';
import type { NavigationService } from 'src/services/NavigationService';
import { UNSAVED_WORKSPACE } from 'src/store/reducers/workspace';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

/**
 * What an unsaved workspace starts from. Templates are references the service
 * resolves, so a spec stays small enough to ride in the URL and survives reloads
 * and back/forward without carrying a stale decoded workspace.
 */
export const newWorkspaceTemplateSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('blank') }),
    z.object({ kind: z.literal('container'), workspaceId: z.string() }),
    z.object({ kind: z.literal('mosaic'), templateId: z.string() }),
]);
export type NewWorkspaceTemplate = z.infer<typeof newWorkspaceTemplateSchema>;

export const newWorkspaceSpecSchema = z.object({
    template: newWorkspaceTemplateSchema,
    name: z.string(),
    layoutSettings: z.object({
        enableMosaicLayout: z.boolean(),
        mosaicGutterPx: z.number(),
        gridSnappingEnabled: z.boolean(),
    }),
});
export type NewWorkspaceSpec = z.infer<typeof newWorkspaceSpecSchema>;

/** A blank mosaic workspace: what a plain `/dashboard/new` starts and what "new blank workspace" requests. */
export const BLANK_NEW_WORKSPACE: NewWorkspaceSpec = {
    template: { kind: 'blank' },
    name: UNSAVED_WORKSPACE.name,
    layoutSettings: {
        enableMosaicLayout: true,
        gridSnappingEnabled: UNSAVED_WORKSPACE.layoutSettings.gridSnappingEnabled,
        mosaicGutterPx: UNSAVED_WORKSPACE.layoutSettings.mosaicGutterPx ?? 0,
    },
};

/**
 * A request to start a new workspace from a spec. Its id makes each request distinct, so entering the
 * same request again names the workspace it started rather than starting another.
 */
export const newWorkspaceRequestSchema = newWorkspaceSpecSchema.extend({ id: z.string().min(1) });
export type NewWorkspaceRequest = z.infer<typeof newWorkspaceRequestSchema>;

/**
 * The `/dashboard/new` search that carries a request. The URL, not history state, is what identifies
 * what the route shows, so reloads, back/forward, and links back to the dashboard all rebuild it.
 */
export const toNewWorkspaceSearch = ({ id, template, name, layoutSettings }: NewWorkspaceRequest): string => {
    const params = new URLSearchParams({ request: id, template: template.kind });
    if (template.kind === 'mosaic') params.set('templateId', template.templateId);
    if (template.kind === 'container') params.set('templateId', template.workspaceId);
    if (name) params.set('name', name);
    params.set('layout', layoutSettings.enableMosaicLayout ? 'mosaic' : 'free');
    params.set('gutter', String(layoutSettings.mosaicGutterPx));
    params.set('snap', layoutSettings.gridSnappingEnabled ? '1' : '0');
    return `?${params}`;
};

const booleanParam = (value: string | null, whenTrue: string, whenFalse: string): boolean | undefined =>
    value === whenTrue ? true : value === whenFalse ? false : undefined;

/** Reads the request a `/dashboard/new` search carries; the URL is untrusted, so anything else is none. */
export const parseNewWorkspaceSearch = (search: string): NewWorkspaceRequest | undefined => {
    const params = new URLSearchParams(search);
    const kind = params.get('template');
    const templateId = params.get('templateId') ?? undefined;
    const gutter = params.get('gutter');
    return newWorkspaceRequestSchema.safeParse({
        id: params.get('request') ?? undefined,
        template:
            kind === 'mosaic'
                ? { kind, templateId }
                : kind === 'container'
                  ? { kind, workspaceId: templateId }
                  : { kind },
        name: params.get('name') ?? '',
        layoutSettings: {
            enableMosaicLayout: booleanParam(params.get('layout'), 'mosaic', 'free'),
            mosaicGutterPx: gutter === null ? undefined : Number(gutter),
            gridSnappingEnabled: booleanParam(params.get('snap'), '1', '0'),
        },
    }).data;
};

/**
 * Enter a new unsaved workspace through the mechanic owned by the current host:
 * web navigates so route entry (and the unsaved-changes guard) owns the
 * transition, native has no routes and loads directly. Each call is a new request.
 */
export const enterNewWorkspace = async (
    deps: { readonly NavigationService?: NavigationService; readonly WorkspaceService: WorkspaceService },
    spec: NewWorkspaceSpec,
): Promise<void> => {
    const request: NewWorkspaceRequest = { ...spec, id: uuidv4() };
    if (deps.NavigationService) {
        await deps.NavigationService.navigate({ pathname: '/dashboard/new', search: toNewWorkspaceSearch(request) });
        return;
    }
    await deps.WorkspaceService.load('new', { newWorkspace: request });
};
